import { Input } from '../Input';
import { Label } from '../Label';

import { useEffect, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../Popover';
import { Button } from '../Button';
import { cn } from '@ais-ui/utils';
import {
  DurationField,
  DurationPickerProps,
  DayTimeDuration,
  DayTimeDurationField,
  DayTimeDurationPickerProps as DayTimeDurationPickerPropsType,
} from './types';

// Helper component for the numeric input with chevron controls
interface DurationInputProps {
  label: string;
  value: number;
  onChange: (value: number) => void;
  onIncrement: () => void;
  onDecrement: () => void;
  min?: number;
  max?: number;
  width?: string;
}

const DurationInput = ({
  label,
  value,
  onChange,
  onIncrement,
  onDecrement,
  min = 0,
  max = 99,
  width = 'w-20',
}: DurationInputProps) => {
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const numValue = parseInt(e.target.value, 10);
    if (isNaN(numValue)) {
      onChange(0);
    } else {
      onChange(Math.min(Math.max(numValue, min), max));
    }
  };

  return (
    <div className="flex flex-col space-y-1">
      <Label className="text-muted-foreground text-xs">{label}</Label>
      <div className="flex items-center space-x-1">
        <Input
          className={cn(
            width,
            'h-9 text-center',
            '[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none',
          )}
          type="number"
          min={min}
          max={max}
          value={value}
          onChange={handleInputChange}
        />
        <div className="flex flex-col">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onIncrement}
            className="h-4 w-6 p-0"
          >
            <ChevronUp className="h-3 w-3" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onDecrement}
            disabled={value <= min}
            className="h-4 w-6 p-0"
          >
            <ChevronDown className="h-3 w-3" />
          </Button>
        </div>
      </div>
    </div>
  );
};

/**
 * A duration picker with separate Days, Hours, and Minutes inputs.
 * Uses structured object values for better validation and UX.
 */
export const DayTimeDurationPickerV2 = ({
  value,
  onChange,
  maxDays = 365,
  maxHours = 23,
  minuteStep = 1,
}: DayTimeDurationPickerPropsType) => {
  // Initialize with defaults if value is undefined
  const currentValue: DayTimeDuration = value ?? {
    days: 0,
    hours: 0,
    minutes: 0,
  };

  const handleFieldChange = (field: DayTimeDurationField, newValue: number) => {
    onChange({
      ...currentValue,
      [field]: newValue,
    });
  };

  const incrementDays = () => {
    if (currentValue.days < maxDays) {
      handleFieldChange('days', currentValue.days + 1);
    }
  };

  const decrementDays = () => {
    if (currentValue.days > 0) {
      handleFieldChange('days', currentValue.days - 1);
    }
  };

  const incrementHours = () => {
    if (currentValue.hours < maxHours) {
      handleFieldChange('hours', currentValue.hours + 1);
    } else {
      // Roll over to next day
      onChange({
        ...currentValue,
        hours: 0,
        days: Math.min(currentValue.days + 1, maxDays),
      });
    }
  };

  const decrementHours = () => {
    if (currentValue.hours > 0) {
      handleFieldChange('hours', currentValue.hours - 1);
    } else if (currentValue.days > 0) {
      // Borrow from days
      onChange({
        ...currentValue,
        hours: maxHours,
        days: currentValue.days - 1,
      });
    }
  };

  const incrementMinutes = () => {
    const newMinutes = currentValue.minutes + minuteStep;
    if (newMinutes >= 60) {
      // Roll over to next hour
      const hoursToAdd = Math.floor(newMinutes / 60);
      const remainingMinutes = newMinutes % 60;
      const newHours = currentValue.hours + hoursToAdd;

      if (newHours > maxHours) {
        onChange({
          days: Math.min(currentValue.days + 1, maxDays),
          hours: newHours - maxHours - 1,
          minutes: remainingMinutes,
        });
      } else {
        onChange({
          ...currentValue,
          hours: newHours,
          minutes: remainingMinutes,
        });
      }
    } else {
      handleFieldChange('minutes', newMinutes);
    }
  };

  const decrementMinutes = () => {
    if (currentValue.minutes >= minuteStep) {
      handleFieldChange('minutes', currentValue.minutes - minuteStep);
    } else if (currentValue.hours > 0 || currentValue.days > 0) {
      // Borrow from hours
      if (currentValue.hours > 0) {
        onChange({
          ...currentValue,
          hours: currentValue.hours - 1,
          minutes: 60 - minuteStep,
        });
      } else if (currentValue.days > 0) {
        onChange({
          days: currentValue.days - 1,
          hours: maxHours,
          minutes: 60 - minuteStep,
        });
      }
    }
  };

  return (
    <div className="flex space-x-3">
      <DurationInput
        label="Days"
        value={currentValue.days}
        onChange={(v) => handleFieldChange('days', v)}
        onIncrement={incrementDays}
        onDecrement={decrementDays}
        min={0}
        max={maxDays}
        width="w-14"
      />
      <DurationInput
        label="Hours"
        value={currentValue.hours}
        onChange={(v) => handleFieldChange('hours', v)}
        onIncrement={incrementHours}
        onDecrement={decrementHours}
        min={0}
        max={maxHours}
        width="w-12"
      />
      <DurationInput
        label="Minutes"
        value={currentValue.minutes}
        onChange={(v) => handleFieldChange('minutes', v)}
        onIncrement={incrementMinutes}
        onDecrement={decrementMinutes}
        min={0}
        max={59}
        width="w-12"
      />
    </div>
  );
};

interface TimeDurationPickerProps {
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  format: 'HH:MM' | 'HH:MM:SS';
  className?: string;
  disabled?: boolean;
}

export const TimeDurationPicker = ({
  value,
  onChange,
  format,
  className,
  disabled,
}: TimeDurationPickerProps) => {
  const [time, setTime] = useState<string | undefined>('');

  // Parse the incoming value
  useEffect(() => {
    setTime(value);
  }, [value]);

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTime = e.target.value === '' ? undefined : e.target.value;

    setTime(newTime);
    onChange(newTime);
  };

  const placeholderText =
    format === 'HH:MM' ? 'Enter time in HH:MM' : 'Enter time in HH:MM:SS';

  return (
    <div className={cn('flex space-x-4', className)}>
      <div className="flex flex-col space-y-2 w-full">
        <Input
          id="time"
          step={format === 'HH:MM' ? '60' : '1'} // Adjust step for seconds input
          value={time || ''}
          onChange={handleTimeChange}
          placeholder={placeholderText}
          disabled={disabled}
        />
      </div>
    </div>
  );
};

export function DurationPicker({
  value,
  onChange,
  maxHours = 24,
  minHours = 0,
  minuteStep = 5,
}: DurationPickerProps) {
  // Use local state that's initialized from props
  const [duration, setDuration] = useState(value);

  // Sync with external value when it changes
  useEffect(() => {
    setDuration(value);
  }, [value]);

  // Helper to call onChange with new values
  const handleChange = (newDuration: { hours: number; minutes: number }) => {
    setDuration(newDuration);
    if (onChange) {
      onChange(newDuration);
    }
  };

  const incrementHours = () => {
    const newDuration = {
      ...duration,
      hours: Math.min(duration.hours + 1, maxHours),
    };
    handleChange(newDuration);
  };

  const decrementHours = () => {
    const newDuration = {
      ...duration,
      hours: Math.max(duration.hours - 1, minHours),
    };
    handleChange(newDuration);
  };

  const incrementMinutes = () => {
    if (duration.minutes === 60 - minuteStep) {
      const newDuration = {
        hours: Math.min(duration.hours + 1, maxHours),
        minutes: 0,
      };
      handleChange(newDuration);
    } else {
      const newDuration = {
        ...duration,
        minutes: duration.minutes + minuteStep,
      };
      handleChange(newDuration);
    }
  };

  const decrementMinutes = () => {
    if (duration.minutes === 0) {
      if (duration.hours > minHours) {
        const newDuration = {
          hours: duration.hours - 1,
          minutes: 60 - minuteStep,
        };
        handleChange(newDuration);
      }
    } else {
      const newDuration = {
        ...duration,
        minutes: duration.minutes - minuteStep,
      };
      handleChange(newDuration);
    }
  };

  const handleInputChange = (field: DurationField, value: string) => {
    const numValue = parseInt(value, 10);

    if (isNaN(numValue)) return;

    if (field === 'hours') {
      const newDuration = {
        ...duration,
        hours: Math.min(Math.max(numValue, minHours), maxHours),
      };
      handleChange(newDuration);
    } else if (field === 'minutes') {
      // Round to nearest step
      const roundedMinutes = Math.round(numValue / minuteStep) * minuteStep;
      const validMinutes = Math.min(Math.max(roundedMinutes, 0), 59);

      const newDuration = {
        ...duration,
        minutes: validMinutes,
      };
      handleChange(newDuration);
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="w-36 justify-between text-left font-normal"
        >
          <span>{`${duration.hours}h ${duration.minutes}m`}</span>
          <div className="flex flex-col">
            <ChevronUp
              className="h-3 w-3 cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                incrementMinutes();
              }}
            />
            <ChevronDown
              className="h-3 w-3 cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                if (duration.hours > 0 || duration.minutes > 0) {
                  decrementMinutes();
                }
              }}
            />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent side="top" className="w-52 p-3" align="end">
        <div className="flex gap-4">
          <div className="flex-1">
            <h4 className="text-sm text-muted-foreground mb-1">Hours</h4>
            <div className="flex space-x-2">
              <Input
                className="w-12 h-8"
                value={duration.hours}
                onChange={(e) => handleInputChange('hours', e.target.value)}
              />
              <div className="flex flex-col space-y-1.5">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={incrementHours}
                  className="h-3 w-3 p-0"
                >
                  <ChevronUp className="size-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={decrementHours}
                  disabled={duration.hours === 0 && duration.minutes === 0}
                  className="h-3 w-3 p-0"
                >
                  <ChevronDown className="size-4" />
                </Button>
              </div>
            </div>
          </div>
          <div className="flex-1">
            <h4 className="text-sm text-muted-foreground mb-1">Minutes</h4>
            <div className="flex space-x-2">
              <Input
                className="w-12 h-8"
                value={duration.minutes}
                onChange={(e) => handleInputChange('minutes', e.target.value)}
              />
              <div className="flex flex-col space-y-1.5">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={incrementMinutes}
                  className="h-3 w-3 p-0"
                >
                  <ChevronUp className="size-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={decrementMinutes}
                  disabled={duration.hours === 0 && duration.minutes === 0}
                  className="h-3 w-3 p-0"
                >
                  <ChevronDown className="size-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
